import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Users, Plus, X } from 'lucide-react';
import { useWorkbookTeamMembers, useRemoveTaskAssignee, useAddTaskAssignee } from '@/hooks/tasks/use-task-assignees';
import { AssignableUser } from '@/types/tasks';
import { useParams } from 'react-router-dom';

interface TaskAssigneeSelectorProps {
  assigneeIds: string[];
  onAssigneesChange: (assigneeIds: string[]) => void;
  isInitialLoading?: boolean;
  taskId?: string; // Add taskId prop for immediate DB operations
}

const TaskAssigneeSelector: React.FC<TaskAssigneeSelectorProps> = ({
  assigneeIds,
  onAssigneesChange,
  isInitialLoading = false,
  taskId
}) => {
  const { id: workbookId } = useParams<{ id: string }>();
  const { data: teamMembers = [], isLoading } = useWorkbookTeamMembers(workbookId);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string>('');
  
  // Mutations for immediate DB operations
  const addAssigneeMutation = useAddTaskAssignee();
  const removeAssigneeMutation = useRemoveTaskAssignee();

  // Get assignee details for display
  const assignees = teamMembers.filter(member => assigneeIds.includes(member.user_id));
  
  // Get available team members (not already assigned)
  const availableMembers = teamMembers.filter(member => !assigneeIds.includes(member.user_id));

  const handleAddAssignee = async (userId: string) => {
    if (userId && !assigneeIds.includes(userId)) {
      // Update local state immediately for responsive UI
      onAssigneesChange([...assigneeIds, userId]);
      setSelectedAssigneeId(''); // Reset selection
      
      // If we have a taskId, also update the database immediately
      if (taskId) {
        try {
          await addAssigneeMutation.mutateAsync({ taskId, assigneeId: userId });
          // Notification is handled by useAddTaskAssignee hook's onSuccess callback
        } catch (error) {
          // Revert local state on error
          onAssigneesChange(assigneeIds);
          console.error('Failed to add assignee to database:', error);
        }
      }
    }
  };

  const handleRemoveAssignee = async (userId: string) => {
    // Update local state immediately for responsive UI
    const newAssigneeIds = assigneeIds.filter(id => id !== userId);
    onAssigneesChange(newAssigneeIds);
    
    // If we have a taskId, also update the database immediately
    if (taskId) {
      try {
        await removeAssigneeMutation.mutateAsync({ taskId, assigneeId: userId });
      } catch (error) {
        // Revert local state on error
        onAssigneesChange(assigneeIds);
        console.error('Failed to remove assignee from database:', error);
      }
    }
  };

  const getInitials = (firstName: string, username: string) => {
    if (firstName) return firstName.charAt(0).toUpperCase();
    if (username) return username.charAt(0).toUpperCase();
    return 'U';
  };

  return (
    <div className="space-y-1">
      <Label htmlFor="assignees">
        Assignees
      </Label>
      
      {/* Current Assignees */}
      {isInitialLoading ? (
        <div className="flex flex-wrap gap-2">
          {[...Array(2)].map((_, i) => (
            <div 
              key={i} 
              className="h-8 w-24 bg-muted animate-pulse rounded-md" 
            />
          ))}
        </div>
      ) : (
        assignees.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {assignees.map((assignee) => (
              <Badge 
                key={assignee.user_id} 
                variant="secondary" 
                className="flex items-center gap-2 px-2 py-1"
              >
                <Avatar className="w-5 h-5">
                  <AvatarImage 
                    src={assignee.profile_picture_url} 
                    alt={assignee.display_name} 
                  />
                  <AvatarFallback className="text-xs">
                    {getInitials(assignee.first_name, assignee.username)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm">{assignee.display_name}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveAssignee(assignee.user_id)}
                  className="ml-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full p-0.5 transition-colors"
                  title={`Remove ${assignee.display_name} from task`}
                  aria-label={`Remove ${assignee.display_name} from task`}
                >
                  <X size={12} />
                </button>
              </Badge>
            ))}
          </div>
        )
      )}

      {/* Add Assignee Dropdown */}
      {!isInitialLoading && availableMembers.length > 0 && (
        <Select 
          value={selectedAssigneeId} 
          onValueChange={(value) => {
            if (value && value !== 'placeholder') {
              handleAddAssignee(value);
            }
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Add team member..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="placeholder" disabled>
              Select a team member
            </SelectItem>
            {isLoading ? (
              <SelectItem value="loading" disabled>
                Loading team members...
              </SelectItem>
            ) : (
              availableMembers.map((member) => (
                <SelectItem key={member.user_id} value={member.user_id}>
                  <div className="flex items-center gap-2">
                    <Avatar className="w-6 h-6">
                      <AvatarImage 
                        src={member.profile_picture_url} 
                        alt={member.display_name} 
                      />
                      <AvatarFallback className="text-xs">
                        {getInitials(member.first_name, member.username)}
                      </AvatarFallback>
                    </Avatar>
                    <span>{member.display_name}</span>
                  </div>
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      )}

      {/* Empty State */}
      {teamMembers.length === 0 && !isLoading && (
        <div className="text-sm text-gray-500 dark:text-gray-400 italic">
          No team members available for assignment
        </div>
      )}

      {/* All members assigned state */}
      {teamMembers.length > 0 && availableMembers.length === 0 && assignees.length > 0 && (
        <div className="text-sm text-gray-500 dark:text-gray-400 italic">
          All team members are assigned to this task
        </div>
      )}
    </div>
  );
};

export default TaskAssigneeSelector; 